import MCPLogo from "@/assets/MCPLogo.svg";
import { Button } from "@/components/ui/button";
import { useHandler, usePersistedPrompts } from "@/hooks";
import { useInputFocus } from "@/hooks";
import { cn } from "@/lib/utils";
import { useInputStore } from "@/state/stores";
import { ScrollArea, Scrollbar, Viewport } from "@radix-ui/react-scroll-area";
import {
  BookKey,
  Bot,
  BotMessageSquare,
  ChevronLeft,
  CloudCog,
  GalleryHorizontalEnd,
} from "lucide-react";
import { memo, useRef } from "react";
import styles from "./SuggestionsTab.module.css";

export const SuggestionsTab = memo(function SuggestionsTab() {
  const setMessage = useInputStore((state) => state.setMessage);
  const focusInput = useInputFocus();
  const viewportElementRef = useRef<HTMLDivElement | null>(null);
  const { setPersistedPrompt } = usePersistedPrompts();

  const handleScroll = useHandler((target: HTMLDivElement) => {
    const { scrollLeft, scrollWidth, clientWidth } = target;
    const maxScrollLeft = scrollWidth - clientWidth;
    const shouldMaskLeft = scrollLeft !== 0;
    const shouldMaskRight = scrollLeft !== maxScrollLeft;

    target.dataset.maskLeft = String(shouldMaskLeft);
    target.dataset.maskRight = String(shouldMaskRight);
  });

  // Set the ref and call handleScroll once on mount
  const handleViewportRef = useHandler((element: HTMLDivElement | null) => {
    viewportElementRef.current = element;
    if (!element) {
      return;
    }
    handleScroll(element);
  });

  const handleScrollButtonClick = useHandler((direction: "left" | "right") => {
    const viewport = viewportElementRef.current;
    if (!viewport) {
      return;
    }
    const scrollDistance = 300; /* arbitrary number, it's about 2 suggestions */
    viewport.scrollLeft +=
      direction === "left" ? -scrollDistance : scrollDistance;
  });

  const handleSuggestionClick = useHandler((prompt: string) => {
    setMessage(prompt);
    setPersistedPrompt(prompt);
    focusInput();
  });

  return (
    <div className={cn("relative overflow-hidden", styles.suggestions)}>
      <ScrollArea data-fp-sug-scroll-area>
        <Viewport
          ref={handleViewportRef}
          onScroll={({ currentTarget }) => handleScroll(currentTarget)}
          className="scroll-smooth"
          data-mask-left="false"
          data-mask-right="true" /* minimize visual funkiness on initial load */
        >
          <div className="flex gap-2">
            {SUGGESTION_PROMPTS.map(({ prompt, suggestion, icon }) => (
              <Button
                key={suggestion}
                variant="outline"
                size="sm"
                className="text-muted-foreground border-dashed border border-border hover:border-border hover:bg-muted hover:text-foreground"
                onClick={() => handleSuggestionClick(prompt)}
              >
                {suggestion}
                {icon}
              </Button>
            ))}
          </div>
        </Viewport>
        <Scrollbar orientation="horizontal" />
      </ScrollArea>

      {/*
        Separating the buttons from the scroll area as the masks otherwise will
        also apply to them
      */}
      {(["left", "right"] as const).map((direction) => (
        <Button
          key={direction}
          variant="ghost"
          size="icon-xs"
          onClick={() => handleScrollButtonClick(direction)}
          className={cn(
            "absolute bottom-0 z-10 w-7 h-7 text-muted-foreground hover:text-foreground hover:bg-muted",
            direction === "left" ? "left-0" : "right-0",
          )}
          data-fp-sug-scroll-button={direction}
        >
          <ChevronLeft className={cn(direction === "right" && "rotate-180")} />
        </Button>
      ))}
    </div>
  );
});

// Placeholder prompts
const SUGGESTION_PROMPTS = [
  {
    suggestion: "List MCP servers",
    prompt:
      "List MCP servers available in the system. Include their name, description, and status.",
    icon: <MCPLogo />,
  },
  {
    suggestion: "Connect MCP",
    prompt: "Help me connect to an MCP server.",
    icon: <MCPLogo />,
  },
  {
    suggestion: "List Endpoints",
    prompt: "List all the endpoints in my app.",
    icon: <GalleryHorizontalEnd />,
  },
  {
    suggestion: "Test endpoints",
    prompt: "Test all the endpoints in my app.",
    icon: <CloudCog />,
  },
  {
    suggestion: "Secrets Setup",
    prompt:
      "List all secrets necessary for the app to run. Also, list which secrets are already set, and which ones are missing.",
    icon: <BookKey />,
  },
  {
    suggestion: "Slack Integration",
    prompt:
      "Setup a Slack Bot integration for the app. Include the webhook URL, channel, and token.",
    icon: <Bot />,
  },
  {
    suggestion: "Discord Integration",
    prompt:
      "Setup a Discord Bot integration for the app. Include the webhook URL, channel, and token.",
    icon: <BotMessageSquare />,
  },
];
